import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import AddCategory from "./AddCategory";
import useCategory from "../../../hooks/useCategory";
import { useSearch } from "../../../components/SearchContext";
import CommonTable from "../../../components/CommonTable";
import { CategoryDetails } from "../../../../../shared/services/interface";
import EditCategory from "./EditCategory";
import { CategoryRow, Table } from "../settingInterface";
import { exportCategory, importCategory } from "../../../../../shared/services/services";
import LoadingScreen from "../../../../../shared/components/LoadingScreen";
import { notifyError, notifySuccess, notifyWarning } from "../../../../../assets/toastService";
import PopupModules from "src/features/auth/components/PopupModules";
import { TablePagination } from "@mui/material";
import { PageTitle } from "src/shared/PageTitle";
import { useUser } from "src/business-types/grocery/components/UserContext";
import iconMap from "src/assets/icons/Icons";

const headCells: Table<CategoryRow>[] = [
  { id: "S.No", label: "S.No", numeric: false, sorting: false },
  { id: "CategoryName", label: "Category", numeric: false },
  { id: "categorySecondName", label: "SecondName", numeric: false },
  { id: "product_count", label: "No. of Products", numeric: true },
];

const Category: React.FC = () => {
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteConfirmList, setDeleteConfirmList] = useState<CategoryDetails[] | null>(null);
  const [editCategory, setEditCategory] = useState<CategoryDetails>();
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { userConfigration } = useUser();
  const [showDropdown, setShowDropdown] = useState(false);
  const DownArrow = iconMap["down"];

  //sorting
  const [orderby, setOrderBy] = useState("");
  const [field, setField] = useState("");

  // pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { searchTerm } = useSearch();
  const { category, loading, categoryPage, DeleteCategory } = useCategory(
    refreshTrigger,
    showDeleted,
    page + 1,
    rowsPerPage,
    searchTerm,
    field,
    orderby,
  );

  useEffect(() => {
    setRowsPerPage(userConfigration?.tableDefultSize || 10);
  }, [userConfigration]);
  useEffect(() => {
    setDeleteConfirmList(null);
  }, [category]);

  // 🧾 Filtered and mapped data
  const tableData: CategoryRow[] = useMemo(
    () =>
      category.map((cat, index) => ({
        "S.No": page * rowsPerPage + index + 1,
        id: cat._id.$oid,
        CategoryName: cat.CategoryName,
        categorySecondName: cat.categorySecondName || "",
        product_count: cat.product_count,
      })),
    [category, page, rowsPerPage],
  );

  // 🧾 Import
  const handleImportClick = () => fileInputRef.current?.click();
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validExtensions = [".xlsx", ".xls", ".csv"];
    if (!validExtensions.some((ext) => file.name.endsWith(ext))) {
      return notifyError("Please upload a valid Excel or CSV file.");
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      await importCategory(formData);
      setRefreshTrigger((prev) => !prev);
      notifySuccess("📁 Category file imported successfully!");
    } catch (error) {
      console.error(error);
      notifyError("Failed to import categories.");
    } finally {
      setIsLoading(false);
    }
  };

  // 🧹 Delete
  const handleDelete = (selectedIds: (string | number)[]) => {
    const selectedCategories = category.filter((p) => selectedIds.includes(p._id.$oid));

    setDeleteConfirmList(selectedCategories);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmList) return;
    DeleteCategory(deleteConfirmList.map((cat) => cat._id.$oid));
  };

  // 📤 Export
  const handleExport = async () => {
    try {
      setIsLoading(true);
      const res = await exportCategory();
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const match = res.headers["content-disposition"]?.match(/filename="?(.+)"?/);
      const fileName = match?.[1] || "category_export.xlsx";

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      notifySuccess("📤 Export successful!");
    } catch (error) {
      console.error(error);
      notifyError("Export failed.");
    } finally {
      setIsLoading(false);
    }
  };

  // ✏️ Edit
  const handleEdit = (id: string | number) => {
    const found = category.find((i) => i._id.$oid === id);
    setEditCategory(found);
  };

  // 🔄 Close
  const closeAll = (data: any) => {
    setAddCategoryOpen(false);
    setEditCategory(undefined);
    setDeleteConfirmList(null);
    setRefreshTrigger((prev) => !prev);
  };

  // 📄 Pagination handlers
  const handlePageChange = (_: any, newPage: number) => setPage(newPage);
  const handleRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (loading || isLoading) return <LoadingScreen />;

  return (
    <div className='w-full h-full flex flex-col overflow-auto justify-start m-1 items-center bg-white rounded-2xl shadow'>
      <PageTitle title='- Settings - Category' />
      {/* 🔘 Actions */}
      <div className='w-full flex justify-end'>
        <div className='w-fit grid grid-cols-3 md:grid-cols-3 gap-2 px-14 pt-2'>
          <label className='flex items-center col-span-3 md:col-span-1 gap-2 cursor-pointer'>
            <input type='checkbox' className='w-5 h-5' checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
            <span>View deleted</span>
          </label>
          <input type='file' accept='.xlsx,.xls,.csv' ref={fileInputRef} className='hidden' onChange={handleFileUpload} />
          <Button label='+ Category' onClick={() => setAddCategoryOpen(true)} variant='ThemeBase' className="p-2 w-fit h-fit mt-4" />
          <Button variant='ThemeBase' onClick={() => { notifyWarning("Comming Zoon") }} icon={DownArrow} iconPosition='right' label='More Options' className="p-2 w-fit h-fit mt-4" />
          {showDropdown && (
            <div className='absolute mt-16 right-16 bg-white border rounded shadow-lg z-10 w-32'>
              <button
                className='w-full px-4 py-2 text-left hover:bg-gray-100'
                onClick={() => {
                  handleExport();
                  setShowDropdown(false);
                }}
              >
                Export
              </button>

              <button
                className='w-full px-4 py-2 text-left hover:bg-gray-100'
                onClick={() => {
                  handleImportClick();
                  setShowDropdown(false);
                }}
              >
                Import
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 🧾 Table */}
      <div className='mx-1 h-fit w-[99%] flex justify-center bg-white rounded-lg shadow-sm'>
        <div className='w-full flex flex-col items-center'>
          <CommonTable<CategoryRow>
            data={tableData}
            headCells={headCells}
            title=''
            onDeleteSelected={handleDelete}
            onEditSelected={handleEdit}
            selectable={false}
            Action={!showDeleted}
            pagenation={false}
            pageSize={rowsPerPage}
            shortByTable={false}
            OrderType={(data) => {
              setField(data.field);
              setOrderBy(data.orderby);
            }}
          />
          <div className='w-[95%] bg-white border rounded-b-md -mt-1 mb-4'>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component='div'
              count={categoryPage?.totalRecords || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsChange}
            />
          </div>
        </div>
      </div>

      {/* 🧩 Modals */}
      {addCategoryOpen && <AddCategory close={closeAll} loading={setIsLoading} addProduct={addCategoryOpen} />}
      {editCategory && <EditCategory editData={editCategory} close={closeAll} loading={setIsLoading} editCategory={!!editCategory} />}

      {/* 🗑️ Delete Confirmation */}
      <PopupModules isOpen={!!deleteConfirmList} onClose={() => setDeleteConfirmList(null)} title='Confirm Deletion'>
        <div className='p-4 flex flex-col gap-4'>
          <div className='text-center'>
            <p className='text-lg font-semibold text-gray-800 mb-2'>Are you sure?</p>
            <p className='text-sm text-gray-600'>
              The following categories will be <span className='font-medium text-red-600'>DELETED</span>. This action cannot be undone.
            </p>
          </div>

          <div className='bg-gray-50 border border-gray-200 rounded-lg max-h-48 overflow-y-auto p-3 shadow-inner'>
            {deleteConfirmList?.map((cat, i) => (
              <div key={cat._id.$oid} className='flex flex-col py-1 px-2 rounded hover:bg-gray-100 transition'>
                <div className='flex items-center gap-2'>
                  <span className='text-gray-500 text-sm font-medium w-6 text-right'>{i + 1}.</span>
                  <span className='text-gray-800 text-sm font-medium'>{cat.CategoryName}</span>
                </div>

                {cat.categorySecondName && <span className='text-xs text-gray-500 ml-8'>{cat.categorySecondName}</span>}
              </div>
            ))}
            {deleteConfirmList?.length === 0 && <p className='text-sm text-gray-400 text-center italic'>No categories selected</p>}
          </div>
          <p className='bg-gray-200 text-red-500 p-2'>
            <b>Note:</b>The products added to the category will be deleted when the category itself is removed
          </p>

          <div className='flex justify-center gap-4 mt-2'>
            <Button label='Cancel' variant='ThemeCancel' className='min-w-[100px] border' onClick={() => setDeleteConfirmList(null)} />
            <Button label='Delete' variant='danger' className='min-w-[100px]' onClick={confirmDelete} />
          </div>
        </div>
      </PopupModules>
    </div>
  );
};

export default Category;
